import React, {
    ReactElement, useCallback, useEffect, useState,
} from "react";
import { tick } from "@apollo/client/testing";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import useSelectionState, { SelectionFocus } from "../../../pages/agent/selection-state";
import useRouting, { Link } from "../../../contexts/Routing";
import {
    Chat,
    DashboardGroupBy, DashboardView, SearchFiltersInput,
    TicketPriority,
    TicketView, useGetChatQuery, useSearchAgentChatLazyQuery,
} from "../../../__generated__/api";
import Loading from "../../loading/Loading";
import Card from "../card/Card";
import { ColumnHeaders } from "../column/Column";
import ScrollableList from "../../scrollable-list/ScrollableList";
import Styles from "../card/Card.module.scss";

function TicketDisplay({ ticketNumber, isSelected }: {ticketNumber: number | undefined, isSelected: boolean}) {
    const { Paths } = useRouting();
    const { data: chatData } = useGetChatQuery({
        skip: ticketNumber == null,
        variables: {
            id: ticketNumber?.toString() ?? "",
        },
    });
    const ticketToDisplay = { ...chatData?.chat, ticketNumber } as Chat;
    const individualTicketStyleClass = [
        "rounded ms-2 p-4",
        Styles["ticket-item"],
        Styles[`ticket-item-${(ticketToDisplay?.priority ?? TicketPriority.Medium).toLowerCase()}`],
        "list-group-item list-group-item-action",
        "border-top-0 border-end-0",
        "d-flex justify-content-start",
    ];
    return (
        <div>
            <h4>Tickets</h4>
            <Link
                to={Paths.AGENT_CHAT}
                params={{ chatId: ticketToDisplay.ticketNumber?.toString() as string }}
                aria-current="true"
                className={`${individualTicketStyleClass.join(" ")}
                            ${(isSelected)
            ? "bg-gray-200 shadow"
            : "shadow bg-white"}`}
            >
                <div className="d-flex">
                    <h6 className="text-light ms-2 mt-1">
                        #
                        {ticketToDisplay?.ticketNumber}
                        {" "}
                    </h6>
                    <h6 className="ms-4 mt-1">
                        {ticketToDisplay?.subject}
                    </h6>
                </div>
            </Link>
        </div>
    );
}

interface TopOptionProps {
    searchValue: string;
    dashboardId: string | undefined;
    ticketView: TicketView | undefined;
    displayedItems: string[];
    groupBy: DashboardGroupBy;
    searchFilters: SearchFiltersInput | undefined;
}

function tryParse(search: string) {
    const parsed = search ? parseInt(search, 10) : undefined;
    return parsed && !Number.isNaN(parsed) ? parsed : undefined;
}

export default function SearchResults({
    searchValue: search, dashboardId, ticketView, displayedItems, groupBy, searchFilters,
}: TopOptionProps): ReactElement | null {
    const { Paths, historyPushWithSearch, getSearchParams } = useRouting();
    const selectionState = useSelectionState();
    const [selectedSearchIndex, setSelectedSearchIndex] = useState(0);
    const ticketNumber = tryParse(search);

    const [searchQuery, { data: searchData, loading: isSearchLoading, fetchMore }] = useSearchAgentChatLazyQuery({
        variables: {
            dashboardId,
            filter: ticketView,
        },
    });

    useEffect(() => {
        const hasSearchFilters = (searchFilters && Object.values(searchFilters).filterNull().isNotEmpty);
        if (search || hasSearchFilters) {
            searchQuery({
                variables: {
                    query: search,
                    filters: hasSearchFilters ? searchFilters : undefined,
                },
            });
        }
    }, [search, searchFilters]);

    const displayedSet = new Set(displayedItems);
    const searchResults = (searchData?.chats?.chats ?? []).filter(result => !displayedSet.has(result.id));

    useEffect(() => {
        if (displayedItems.isNotEmpty) {
            selectionState.selectByIndex(0, SelectionFocus.NONE);
        } else {
            selectionState.unselectAll();
        }

        if (searchResults.isNotEmpty) {
            selectionState.setSearchResults(searchResults);
        }

        setSelectedSearchIndex(0);
    }, [searchData]);

    const toggleNumberTicketSearchIndex = ticketNumber ? 1 : 0;
    const totalResults = displayedItems.length + searchResults.length + toggleNumberTicketSearchIndex;

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "down",
            context: "search",
            shouldUseAllContexts: true,
            handler: () => {
                const next = Math.min(selectedSearchIndex + 1, totalResults - 1);
                if (next < displayedItems.length) {
                    selectionState.selectByIndex(next, SelectionFocus.NONE);
                } else {
                    selectionState.unselectAll();
                }
                setSelectedSearchIndex(next);
            },
        });
        shortcuts.on({
            shortcut: "up",
            context: "search",
            shouldUseAllContexts: true,
            handler: () => {
                const next = Math.max(selectedSearchIndex - 1, 0);
                if (next < displayedItems.length) {
                    selectionState.selectByIndex(next, SelectionFocus.NONE);
                } else {
                    selectionState.unselectAll();
                }
                setSelectedSearchIndex(next);
            },
        });

        shortcuts.on({
            shortcut: "enter",
            context: "search",
            shouldUseAllContexts: true,
            handler: () => {
                let chatId;
                if (selectedSearchIndex < displayedItems.length) {
                    const ticket = selectionState.getSelection().firstElement;
                    chatId = ticket.ticketNumber ?? ticket?.id;
                } else if (selectedSearchIndex === displayedItems.length) {
                    chatId = ticketNumber;
                } else {
                    const result = searchResults[selectedSearchIndex - displayedItems.length - toggleNumberTicketSearchIndex];
                    chatId = result.ticketNumber ?? result?.id;
                }

                if (!chatId) {
                    return;
                }
                historyPushWithSearch(Paths.AGENT_CHAT, { search }, {
                    chatId,
                });
            },
        });
    });

    const handleScroll = useCallback(() => {
        if (searchData?.chats?.nextPageToken) {
            fetchMore({
                variables: {
                    nextPageToken: searchData?.chats?.nextPageToken,
                },
                updateQuery(previous, { fetchMoreResult }) {
                    return {
                        chats: {
                            chats: (previous.chats?.chats ?? []).concat(fetchMoreResult.chats?.chats ?? []).dedupe(row => row.id),
                            nextPageToken: fetchMoreResult.chats?.nextPageToken,
                        },
                        __typename: fetchMoreResult.__typename,
                    };
                },
            });
        }
    }, [searchData?.chats?.nextPageToken]);

    if (!isSearchLoading
             && (search || Object.values(searchFilters ?? {}).isNotEmpty)
             && displayedItems.isEmpty
             && searchResults.isEmpty) {
        return (
            <div className="p-3">
                {ticketNumber
                && (
                    <TicketDisplay
                        ticketNumber={ticketNumber}
                        isSelected={selectedSearchIndex === displayedItems.length}
                    />
                )}
                {search ? (
                    <span className="mt-4 d-block text-center">
                        No Results for &quot;
                        {search}
                        &quot;
                    </span>
                ) : (
                    <span className="mt-4 d-block text-center">
                        No Results for your current filters.
                    </span>
                )}

            </div>
        );
    }

    if ((!search && !Object.values(searchFilters ?? {}).filterNull().isNotEmpty) || (!ticketNumber && searchResults.isEmpty)) {
        return null;
    }

    return (
        <div className="p-2 ms-2">
            {ticketNumber
                && (
                    <TicketDisplay
                        ticketNumber={ticketNumber}
                        isSelected={selectedSearchIndex === displayedItems.length}
                    />
                )}
            {isSearchLoading
                && (
                    <Loading />
                )}
            {!isSearchLoading
                && (
                    <div>
                        <h4>
                            {displayedItems.isNotEmpty ? "Other" : ""}
                            {" "}
                            Results
                        </h4>
                        <ColumnHeaders groupBy={groupBy} ticketView={ticketView ?? TicketView.MyTickets} />

                        <ScrollableList onScroll={handleScroll} className="mt-3">

                            {searchResults.map((result, index) => (
                                <Card
                                    groupBy={groupBy}
                                    view={DashboardView.Compact}
                                    value={result}
                                    registerCardPrefix="search."
                                    className={
                                        (selectedSearchIndex === index + displayedItems.length + toggleNumberTicketSearchIndex)
                                            ? "bg-gray-200 shadow"
                                            : "shadow"
                                    }
                                    searchParams={getSearchParams(Paths.AGENT)}
                                    ticketView={ticketView ?? TicketView.MyTickets}
                                    hasSlas={false}
                                    preview={(
                                        <div>
                                            {result.searchContext?.previews?.firstElement?.previews?.map(preview => (
                                                // eslint-disable-next-line react/no-danger
                                                <div dangerouslySetInnerHTML={{ __html: preview }} />
                                            ))}
                                        </div>
                                    )}
                                />

                            ))}
                        </ScrollableList>
                    </div>
                )}
            <div className="list-group list-group-flush mt-2" />

        </div>
    );
}
